import type { Paragraph as DocxParagraph } from 'docx';

export const exportResumeToPdf = async (element: HTMLElement, fileName: string): Promise<void> => {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf')
  ]);

  // A4 dimensions in mm
  const PAGE_WIDTH_MM = 210;
  const PAGE_HEIGHT_MM = 297;
  const MARGIN_MM = 10;
  const CONTENT_WIDTH_MM = PAGE_WIDTH_MM - MARGIN_MM * 2;
  const CONTENT_HEIGHT_MM = PAGE_HEIGHT_MM - MARGIN_MM * 2;

  const prevBorder = element.style.border;
  const prevBorderRadius = element.style.borderRadius;
  element.style.border = 'none';
  element.style.borderRadius = '0';

  const hiddenEls = Array.from(element.querySelectorAll<HTMLElement>('[data-pdf-hide]'));
  hiddenEls.forEach((el) => (el.style.visibility = 'hidden'));

  const canvas = await html2canvas(element, { scale: 2, useCORS: true });

  hiddenEls.forEach((el) => (el.style.visibility = ''));
  element.style.border = prevBorder;
  element.style.borderRadius = prevBorderRadius;

  // Pixels per mm on the canvas (based on content width)
  const pxPerMm = canvas.width / CONTENT_WIDTH_MM;
  const pageHeightPx = Math.floor(CONTENT_HEIGHT_MM * pxPerMm);
  const totalHeightPx = canvas.height;

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Find the best pixel row to break at — scan upward from the ideal cut point
  // looking for a row that is fully white (empty space between sections).
  const findBreakRow = (
    idealRow: number,
    searchRange: number,
    ctx: CanvasRenderingContext2D
  ): number => {
    for (let offset = 0; offset <= searchRange; offset++) {
      for (const direction of [1, -1]) {
        const row = idealRow + offset * direction;
        if (row < 0 || row >= totalHeightPx) continue;
        const pixels = ctx.getImageData(0, row, canvas.width, 1).data;
        let isBlank = true;
        for (let i = 0; i < pixels.length; i += 4) {
          // Consider a pixel "non-blank" if it's not near-white
          if (pixels[i] < 240 || pixels[i + 1] < 240 || pixels[i + 2] < 240) {
            isBlank = false;
            break;
          }
        }
        if (isBlank) return row;
      }
    }
    // No blank row found — fall back to ideal cut
    return idealRow;
  };

  const offscreenCanvas = document.createElement('canvas');
  offscreenCanvas.width = canvas.width;
  offscreenCanvas.height = canvas.height;
  const ctx = offscreenCanvas.getContext('2d')!;
  ctx.drawImage(canvas, 0, 0);

  let startRow = 0;
  let pageIndex = 0;

  while (startRow < totalHeightPx) {
    const idealEnd = startRow + pageHeightPx;
    let endRow: number;

    if (idealEnd >= totalHeightPx) {
      endRow = totalHeightPx;
    } else {
      // Search up to 5% of page height for a natural break
      const searchRange = Math.floor(pageHeightPx * 0.05);
      endRow = findBreakRow(idealEnd, searchRange, ctx);
    }

    const sliceHeightPx = endRow - startRow;
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;
    const sliceCtx = sliceCanvas.getContext('2d')!;
    sliceCtx.drawImage(
      offscreenCanvas,
      0,
      startRow,
      canvas.width,
      sliceHeightPx,
      0,
      0,
      canvas.width,
      sliceHeightPx
    );

    const imgData = sliceCanvas.toDataURL('image/png');
    const sliceHeightMm = sliceHeightPx / pxPerMm;

    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(imgData, 'PNG', MARGIN_MM, MARGIN_MM, CONTENT_WIDTH_MM, sliceHeightMm);

    startRow = endRow;
    pageIndex++;
  }

  pdf.save(fileName);
};

export const exportResumeToDocx = async (resume: Resume, fileName: string): Promise<void> => {
  const {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    AlignmentType,
    TabStopPosition,
    TabStopType
  } = await import('docx');

  const pt = (points: number) => points * 20;

  const divider = () =>
    new Paragraph({ text: '', thematicBreak: true, spacing: { before: pt(4), after: pt(4) } });

  const sectionHeading = (text: string) =>
    new Paragraph({
      text,
      heading: HeadingLevel.HEADING_2,
      spacing: { before: pt(8), after: pt(6) }
    });

  const { name, linkedin, email, phone, location } = resume.personal_information;
  const contactParts: string[] = [];
  if (linkedin) contactParts.push(linkedin.replace('https://www.', ''));
  if (email) contactParts.push(email);
  if (phone) contactParts.push(phone);
  if (location) contactParts.push(location);

  const summaryParagraphs: DocxParagraph[] = resume.summary
    ? [
        new Paragraph({
          children: [new TextRun({ text: resume.summary, size: 22 })],
          spacing: { before: pt(4), after: pt(6) }
        })
      ]
    : [];

  // Work experience
  const experienceParagraphs: DocxParagraph[] = [];
  for (const exp of resume.work_experiences) {
    experienceParagraphs.push(
      new Paragraph({
        tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
        children: [
          new TextRun({ text: exp.job_role, bold: true, size: 24 }),
          new TextRun({ text: '\t' }),
          new TextRun({ text: exp.company_name, bold: true, size: 24 })
        ],
        spacing: { before: pt(8), after: pt(2) }
      })
    );
    const metaParts: string[] = [];
    if (exp.location) metaParts.push(exp.location);
    if (exp.time) metaParts.push(exp.time);
    const metaText = metaParts.join(' | ') + (exp.duration ? ` • ${exp.duration}` : '');
    if (metaText.trim()) {
      experienceParagraphs.push(
        new Paragraph({
          children: [new TextRun({ text: metaText, italics: true, size: 20, color: '838383' })],
          spacing: { before: 0, after: pt(4) }
        })
      );
    }
    for (const desc of exp.description) {
      experienceParagraphs.push(
        new Paragraph({
          children: [new TextRun({ text: desc, size: 22 })],
          bullet: { level: 0 },
          spacing: { before: pt(1), after: pt(1) }
        })
      );
    }
  }

  // Education
  const educationParagraphs: DocxParagraph[] = [];
  for (const edu of resume.educations) {
    educationParagraphs.push(
      new Paragraph({
        tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
        children: [
          new TextRun({ text: edu.school_name, bold: true, size: 22 }),
          ...(edu.location
            ? [
                new TextRun({ text: '\t' }),
                new TextRun({ text: edu.location, size: 20, color: '838383' })
              ]
            : [])
        ],
        spacing: { before: pt(8), after: pt(2) }
      })
    );
    const degreeParts: string[] = [];
    if (edu.degree) degreeParts.push(edu.degree);
    if (edu.department) degreeParts.push(`in ${edu.department}`);
    const degreeLine = degreeParts.join(' ') + (edu.time ? ` | ${edu.time}` : '');
    if (degreeLine.trim()) {
      educationParagraphs.push(
        new Paragraph({
          children: [new TextRun({ text: degreeLine, italics: true, size: 20, color: '838383' })],
          spacing: { before: 0, after: pt(4) }
        })
      );
    }
  }

  // Group items into rows of `perRow` when there are many items, otherwise one per bullet.
  const COMPACT_THRESHOLD = 4;
  const ITEMS_PER_ROW = 3;
  const groupedBullets = (contents: string[]): DocxParagraph[] => {
    if (contents.length < COMPACT_THRESHOLD) {
      return contents.map(
        (content) =>
          new Paragraph({
            children: [new TextRun({ text: content, size: 22 })],
            bullet: { level: 0 },
            spacing: { before: pt(1), after: pt(1) }
          })
      );
    }
    const rows: DocxParagraph[] = [];
    for (let i = 0; i < contents.length; i += ITEMS_PER_ROW) {
      const row = contents.slice(i, i + ITEMS_PER_ROW).join('   •   ');
      rows.push(
        new Paragraph({
          children: [new TextRun({ text: row, size: 22 })],
          bullet: { level: 0 },
          spacing: { before: pt(1), after: pt(1) }
        })
      );
    }
    return rows;
  };

  // Skills
  const skillParagraphs: DocxParagraph[] = [];
  for (const skill of resume.skills) {
    if (skill.section.toLowerCase() !== 'skills') {
      skillParagraphs.push(
        new Paragraph({
          children: [new TextRun({ text: skill.section, bold: true, size: 22 })],
          spacing: { before: pt(6), after: pt(2) }
        })
      );
    }
    skillParagraphs.push(...groupedBullets(skill.contents));
  }

  // Other information
  const otherParagraphs: DocxParagraph[] = [];
  for (const info of resume.other_information) {
    otherParagraphs.push(
      new Paragraph({
        children: [new TextRun({ text: info.section, bold: true, size: 22 })],
        spacing: { before: pt(6), after: pt(2) }
      })
    );
    otherParagraphs.push(...groupedBullets(info.contents));
  }

  const children: DocxParagraph[] = [
    new Paragraph({
      text: name,
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: pt(4) }
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 0, after: pt(8) },
      children: [new TextRun({ text: contactParts.join(' | '), size: 20, color: '838383' })]
    }),
    ...summaryParagraphs,
    divider(),
    sectionHeading('Professional Experience'),
    ...experienceParagraphs,
    divider(),
    sectionHeading('Education'),
    ...educationParagraphs,
    ...(resume.skills.length > 0 ? [divider(), sectionHeading('Skills'), ...skillParagraphs] : []),
    ...(resume.other_information.length > 0
      ? [divider(), sectionHeading('Other Information'), ...otherParagraphs]
      : [])
  ];

  const doc = new Document({ sections: [{ properties: {}, children }] });
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const getFileSize = (size: number): string => {
  if (size === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(size) / Math.log(k));
  const value = size / Math.pow(k, i);
  let formatted = value.toFixed(2);

  // Remove trailing .00 for whole numbers
  if (formatted.endsWith('.00')) {
    formatted = formatted.slice(0, -3);
  }

  return `${formatted} ${sizes[i]}`;
};
